import React, { useState } from 'react';
import config from './config';

const FTOAssessmentPage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  
  // Form data
  const [formData, setFormData] = useState({
    productName: '',
    description: '',
    features: '',
    market: ''
  });
  
  // Results
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.productName.trim() || !formData.description.trim() || !formData.features.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setStep(2);
    setProgress({ percent: 10, message: 'Analyzing your product...' });

    try {
      const token = localStorage.getItem('token');
      
      // Progress updates
      const progressUpdates = [
        { percent: 20, message: 'Identifying key technical features...' },
        { percent: 35, message: 'Searching for potentially blocking patents...' },
        { percent: 50, message: 'Analyzing patent claims...' },
        { percent: 70, message: 'Assessing infringement risk...' },
        { percent: 85, message: 'Generating design-around suggestions...' },
        { percent: 95, message: 'Preparing assessment...' }
      ];
      
      let progressIndex = 0;
      const progressInterval = setInterval(() => {
        if (progressIndex < progressUpdates.length) {
          setProgress(progressUpdates[progressIndex]);
          progressIndex++;
        }
      }, 1500);

      const response = await fetch(`${config.API_URL}/api/fto-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: formData.productName,
          description: formData.description,
          features: formData.features,
          market: formData.market,
          token: token
        })
      });

      clearInterval(progressInterval);
      
      const data = await response.json();
      
      if (data.success) {
        setResults(data);
        setStep(3);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Error analyzing FTO');
        setStep(1);
      }
    } catch (err) {
      setError('Error connecting to server. Please try again.');
      setStep(1);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetJourney = () => {
    setStep(1);
    setFormData({ productName: '', description: '', features: '', market: '' });
    setResults(null);
    setError('');
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'LOW': return { bg: '#dcfce7', border: '#16a34a', text: '#166534', icon: '🟢' };
      case 'MEDIUM': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', icon: '🟡' };
      case 'HIGH': return { bg: '#fef2f2', border: '#dc2626', text: '#dc2626', icon: '🔴' };
      default: return { bg: '#f1f5f9', border: '#64748b', text: '#475569', icon: '⚪' };
    }
  };

  const getPatentRiskBadge = (risk) => {
    const colors = {
      'High': { bg: '#fef2f2', text: '#dc2626' },
      'Medium': { bg: '#fef3c7', text: '#92400e' },
      'Low': { bg: '#dcfce7', text: '#166534' }
    };
    return colors[risk] || colors['Medium'];
  };

  // Save report to database
  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'fto-assessment',
          title: formData?.productName || 'FTO Assessment Report',
          input_data: formData,
          results_data: results,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  // Download as HTML
  const downloadHTML = () => {
    const riskColors = {
      LOW: { bg: '#dcfce7', border: '#16a34a', text: '#166534' },
      MEDIUM: { bg: '#fef3c7', border: '#d97706', text: '#92400e' },
      HIGH: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' }
    };
    const risk = results?.overall_risk || 'MEDIUM';
    const colors = riskColors[risk] || riskColors.MEDIUM;

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>FTO Assessment: ${formData?.productName || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    h1 { color: #1e293b; border-bottom: 3px solid #0369a1; padding-bottom: 10px; }
    h2 { color: #0369a1; margin-top: 30px; }
    .risk-box { background: ${colors.bg}; border-left: 4px solid ${colors.border}; padding: 20px; margin: 20px 0; border-radius: 8px; }
    .risk-label { font-size: 24px; font-weight: bold; color: ${colors.text}; }
    .risk-explanation { color: ${colors.text}; margin-top: 10px; }
    .product-details { background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .product-details p { margin: 10px 0; }
    .summary-box { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .patent-card { background: white; border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; margin: 10px 0; }
    .patent-card h4 { margin: 0 0 10px 0; color: #1e293b; }
    .risk-high { color: #dc2626; font-weight: bold; }
    .risk-medium { color: #d97706; font-weight: bold; }
    .risk-low { color: #16a34a; font-weight: bold; }
    .feature-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    .feature-table th, .feature-table td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    .feature-table th { background: #f8fafc; }
    .status-clear { color: #16a34a; font-weight: bold; }
    .status-caution { color: #d97706; font-weight: bold; }
    .status-risk { color: #dc2626; font-weight: bold; }
    .list-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .list-section ul, .list-section ol { margin: 10px 0; padding-left: 25px; }
    .list-section li { margin: 8px 0; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Freedom to Operate Assessment</h1>

  <div class="risk-box">
    <div class="risk-label">${risk === 'LOW' ? '🟢 Low Infringement Risk' : risk === 'HIGH' ? '🔴 High Infringement Risk' : '🟡 Medium Infringement Risk'}</div>
    <div class="risk-explanation">${results?.risk_explanation || ''}</div>
  </div>

  <h2>Product Details</h2>
  <div class="product-details">
    <p><strong>Product Name:</strong> ${formData?.productName || 'Not specified'}</p>
    <p><strong>Description:</strong> ${formData?.description || 'Not specified'}</p>
    <p><strong>Key Features:</strong> ${formData?.features || 'Not specified'}</p>
    <p><strong>Target Market:</strong> ${formData?.market || 'United States'}</p>
  </div>

  <h2>Assessment Summary</h2>
  <div class="summary-box">
    <p>${results?.summary || 'No summary available'}</p>
  </div>

  ${results?.feature_analysis && results.feature_analysis.length > 0 ? `
    <h2>Feature Analysis</h2>
    <table class="feature-table">
      <thead>
        <tr><th>Feature</th><th>Status</th></tr>
      </thead>
      <tbody>
        ${results.feature_analysis.map(f => `
          <tr>
            <td>${f.feature}</td>
            <td class="status-${(f.status || 'caution').toLowerCase()}">${f.status || 'Unknown'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  ` : ''}

  ${results?.blocking_patents && results.blocking_patents.length > 0 ? `
    <h2>Potentially Blocking Patents</h2>
    ${results.blocking_patents.map(p => `
      <div class="patent-card">
        <h4>${p.patent_number}: ${p.title}</h4>
        <p><strong>Risk Level:</strong> <span class="risk-${(p.risk_level || 'medium').toLowerCase()}">${p.risk_level || 'Unknown'}</span></p>
        <p><strong>Relevant Claims:</strong> ${p.relevant_claims || 'Not specified'}</p>
        <p><strong>Concern:</strong> ${p.concern || ''}</p>
      </div>
    `).join('')}
  ` : '<h2>Blocking Patents</h2><p>No blocking patents identified in initial search.</p>'}

  <h2>Design-Around Suggestions</h2>
  <div class="list-section">
    <ul>
      ${(results?.design_around || []).map(item => `<li>💡 ${item}</li>`).join('') || '<li>No specific design-around suggestions</li>'}
    </ul>
  </div>

  <h2>Recommended Next Steps</h2>
  <div class="list-section">
    <ol>
      ${(results?.next_steps || []).map(step => `<li>${step}</li>`).join('') || '<li>Consult with a patent attorney for detailed FTO analysis</li>'}
    </ol>
  </div>

  <div class="footer">
    <p><strong>Critical Disclaimer:</strong> This is an automated preliminary screening, NOT a legal opinion. FTO analysis requires detailed claim-by-claim review by a qualified patent attorney. Do not make business decisions based solely on this report.</p>
    <p>Generated by PatTrak - ${new Date().toLocaleDateString()}</p>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PatTrak_FTO_${(formData?.productName || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as PDF
  const downloadPDF = async () => {
    if (!results) return;
    const risk = results?.overall_risk || 'MEDIUM';
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>FTO Assessment: ${formData?.productName || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #1e293b; font-size: 24px; }
    h2 { color: #0369a1; font-size: 18px; border-bottom: 2px solid #0369a1; padding-bottom: 8px; margin-top: 30px; }
    .section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .risk-box { padding: 15px; border-radius: 8px; margin: 15px 0; }
    .risk-low { background: #dcfce7; border-left: 4px solid #16a34a; }
    .risk-medium { background: #fef3c7; border-left: 4px solid #d97706; }
    .risk-high { background: #fee2e2; border-left: 4px solid #dc2626; }
    ul, ol { margin: 10px 0; padding-left: 20px; }
    li { margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    th { background: #f8fafc; }
    .patent-card { background: white; border: 1px solid #e2e8f0; padding: 12px; margin: 10px 0; border-radius: 6px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>FTO Assessment Report</h1>
  <p><strong>Product:</strong> ${formData?.productName || 'Not specified'}</p>
  <p><strong>Market:</strong> ${formData?.market || 'United States'}</p>
  <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>

  <div class="risk-box risk-${risk.toLowerCase()}">
    <strong>Risk Level: ${risk}</strong>
    <p>${results?.risk_explanation || ''}</p>
  </div>

  <h2>Assessment Summary</h2>
  <div class="section">${results?.summary || ''}</div>

  ${results?.feature_analysis && results.feature_analysis.length > 0 ? '<h2>Feature Analysis</h2><table><tr><th>Feature</th><th>Status</th></tr>' + results.feature_analysis.map(f => '<tr><td>' + f.feature + '</td><td>' + f.status + '</td></tr>').join('') + '</table>' : ''}

  ${results?.blocking_patents && results.blocking_patents.length > 0 ? '<h2>Potentially Blocking Patents</h2>' + results.blocking_patents.map(p => '<div class="patent-card"><strong>' + p.patent_number + ':</strong> ' + p.title + '<br/><small>Risk: ' + p.risk_level + ' | Claims: ' + (p.relevant_claims || 'N/A') + '</small><p>' + (p.concern || '') + '</p></div>').join('') : ''}

  <h2>Design-Around Suggestions</h2>
  <div class="section"><ul>${(results?.design_around || []).map(item => '<li>' + item + '</li>').join('') || '<li>No suggestions</li>'}</ul></div>

  <h2>Recommended Next Steps</h2>
  <div class="section"><ol>${(results?.next_steps || []).map(item => '<li>' + item + '</li>').join('') || '<li>Consult patent attorney</li>'}</ol></div>

  <div class="footer">Generated by PatTrak - Patent Intelligence Made Simple<br/>This is not legal advice.</div>
</body>
</html>`;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-invention-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_html: htmlContent,
          title: formData?.productName || 'FTO_Assessment'
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_FTO_${(formData?.productName || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    }
  };

  // Tier gate - only Premium and Pro users can access
  if (!user || user.tier === 'free') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              FTO Assessment
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Analyze freedom to operate risk for your product or technology.
            </p>
            
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '20px', marginBottom: '32px' }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                {!user 
                  ? 'Please register or log in to access this tool.'
                  : 'This tool requires a Premium or Pro subscription.'
                }
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              {!user ? (
                <>
                  <button onClick={() => setPage('register')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Register Free
                  </button>
                  <button onClick={() => setPage('login')} style={{ padding: '14px 28px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Log In
                  </button>
                </>
              ) : (
                <button onClick={() => setPage('pricing')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                  Upgrade to Premium
                </button>
              )}
              <button onClick={onBack} style={{ padding: '10px', background: 'none', color: '#64748b', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>
        
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          ⚖️ FTO Assessment
        </h1>
        <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
          Check if your product might infringe existing patents
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '32px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        {[
          { num: 1, label: 'Describe Product' },
          { num: 2, label: 'Analyzing' },
          { num: 3, label: 'Risk Assessment' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              opacity: step >= s.num ? 1 : 0.4
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: step > s.num ? '#16a34a' : step === s.num ? '#0369a1' : '#e2e8f0',
                color: step >= s.num ? 'white' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: step === s.num ? '600' : '400',
                color: step === s.num ? '#0369a1' : '#64748b'
              }}>
                {s.label}
              </span>
            </div>
            {idx < 2 && (
              <div style={{ 
                flex: 1, 
                height: '2px', 
                background: step > s.num ? '#16a34a' : '#e2e8f0',
                margin: '0 16px'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Describe Product */}
      {step === 1 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Tell me about your product
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            I'll search for patents that might block you from making or selling it.
          </p>

          {/* Product Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Product/Process Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.productName}
              onChange={(e) => handleInputChange('productName', e.target.value)}
              placeholder="e.g., SmartCool Thermostat, BioClean Water Filter"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              What does it do? <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your product or process. What problem does it solve? How does it work?"
              rows={4}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Key Features */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Key Technical Features <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={formData.features}
              onChange={(e) => handleInputChange('features', e.target.value)}
              placeholder="List the main technical components or methods (one per line):
- Sensor type and placement
- Communication protocol
- Control algorithm
- Materials used"
              rows={4}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Target Market (optional) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Target Market <span style={{ color: '#94a3b8' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={formData.market}
              onChange={(e) => handleInputChange('market', e.target.value)}
              placeholder="e.g., United States, Europe, Global"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!formData.productName.trim() || !formData.description.trim() || !formData.features.trim()}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              background: (!formData.productName.trim() || !formData.description.trim() || !formData.features.trim()) 
                ? '#94a3b8' : '#0369a1',
              border: 'none',
              borderRadius: '8px',
              cursor: (!formData.productName.trim() || !formData.description.trim() || !formData.features.trim()) 
                ? 'not-allowed' : 'pointer'
            }}
          >
            Check Freedom to Operate →
          </button>

          {/* Learning moment */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #fcd34d'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
              ⚖️ What is Freedom to Operate (FTO)?
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: '#78350f', lineHeight: '1.6' }}>
              FTO analysis determines whether your product might infringe existing patents. 
              Even if your invention is patentable, you might still infringe someone else's patent 
              when you make, use, or sell it. This is different from patentability.
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 2 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚖️</div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Analyzing Patent Landscape...
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            {progress.message}
          </p>
          
          {/* Progress bar */}
          <div style={{
            width: '300px',
            height: '8px',
            background: '#e2e8f0',
            borderRadius: '4px',
            margin: '0 auto 16px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress.percent}%`,
              height: '100%',
              background: '#0369a1',
              borderRadius: '4px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {progress.percent}% complete
          </div>

          {/* What's happening */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            textAlign: 'left'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              What's happening:
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#64748b' }}>
              <li>Extracting key features from your product description</li>
              <li>Searching 1.45 million USPTO patents for potential blockers</li>
              <li>Analyzing patent claims against your features</li>
              <li>Assessing infringement risk levels</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          {/* Risk Banner */}
          {(() => {
            const risk = getRiskColor(results.overall_risk);
            return (
              <div style={{
                background: risk.bg,
                border: `2px solid ${risk.border}`,
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>{risk.icon}</div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: risk.text, margin: 0 }}>
                  {results.overall_risk === 'LOW' && 'Low Infringement Risk'}
                  {results.overall_risk === 'MEDIUM' && 'Moderate Infringement Risk'}
                  {results.overall_risk === 'HIGH' && 'High Infringement Risk'}
                </h2>
                <p style={{ color: risk.text, marginTop: '8px', fontSize: '16px' }}>
                  {results.risk_explanation}
                </p>
              </div>
            );
          })()}

          {/* Summary */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
              📋 Assessment Summary
            </h3>
            <p style={{ fontSize: '15px', color: '#475569', lineHeight: '1.6', margin: 0 }}>
              {results.summary}
            </p>
          </div>

          {/* Potentially Blocking Patents */}
          {results.blocking_patents && results.blocking_patents.length > 0 && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                🚫 Potentially Blocking Patents ({results.blocking_patents.length})
              </h3>
              {results.blocking_patents.map((patent, idx) => (
                <div key={idx} style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  marginBottom: idx < results.blocking_patents.length - 1 ? '12px' : 0,
                  borderLeft: `4px solid ${patent.risk_level === 'High' ? '#dc2626' : 
                                          patent.risk_level === 'Medium' ? '#f59e0b' : '#16a34a'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {patent.patent_number}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                        {patent.title}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      background: getPatentRiskBadge(patent.risk_level).bg,
                      color: getPatentRiskBadge(patent.risk_level).text,
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {patent.risk_level} Risk
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                    <strong>Relevant Claims:</strong> {patent.relevant_claims}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    <strong>Why it matters:</strong> {patent.concern}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Your Features Analysis */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              🔍 Your Features Analysis
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              {results.feature_analysis?.map((feature, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: feature.status === 'Clear' ? '#f0fdf4' : 
                              feature.status === 'Caution' ? '#fef3c7' : '#fef2f2',
                  borderRadius: '8px'
                }}>
                  <span style={{ fontSize: '14px', color: '#374151' }}>{feature.feature}</span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: feature.status === 'Clear' ? '#166534' : 
                           feature.status === 'Caution' ? '#92400e' : '#dc2626'
                  }}>
                    {feature.status === 'Clear' ? 'Likely Clear' : 
                     feature.status === 'Caution' ? '⚠️ Needs Review' : '⚠️ Potential Issue'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Design-Around Suggestions */}
          {results.design_around && results.design_around.length > 0 && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', marginBottom: '12px' }}>
                💡 Design-Around Suggestions
              </h3>
              <ul style={{ margin: 0, paddingLeft: '24px', color: '#0c4a6e', fontSize: '14px', lineHeight: '2' }}>
                {results.design_around.map((suggestion, idx) => (
                  <li key={idx}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended Next Steps */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
              📝 Recommended Next Steps
            </h3>
            <ol style={{ margin: 0, paddingLeft: '24px', color: '#475569', fontSize: '14px', lineHeight: '2' }}>
              {results.next_steps?.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Disclaimer */}
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ fontSize: '12px', color: '#991b1b' }}>
              <strong>⚠️ Critical Disclaimer:</strong> This is an automated preliminary screening, NOT a legal opinion. 
              FTO analysis requires detailed claim-by-claim review by a qualified patent attorney. 
              This assessment covers USPTO patents only. Do not make business decisions based solely on this report.
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <button
              onClick={resetJourney}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#0369a1',
                background: 'white',
                border: '2px solid #0369a1',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onClick={() => { setResults(null); setStep(1); setFormData({ productName: '', description: '', features: '', market: '' }); }}
            >
              Check Another Product
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: saveSuccess ? '#16a34a' : '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
            </button>
            <button
              onClick={downloadHTML}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download HTML
            </button>
            <button
              onClick={downloadPDF}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download PDF
            </button>
          </div>

          {/* What You Learned */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              📚 What you learned
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#64748b' }}>
              <li>The difference between patentability and freedom to operate</li>
              <li>How to identify potentially blocking patents</li>
              <li>Design-around strategies to reduce infringement risk</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default FTOAssessmentPage;